import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "../features/auth/context/AuthContext";
import { ThemeProvider } from "../context/ThemeContext";
import { I18nProvider } from "../i18n";
import { ToastProvider } from "../components/ui/Toast";
import RealtimeEventsBridge from "../features/realtime/RealtimeEventsBridge";
import { apiRetryDelay } from "../lib/apiRetry";
import SupportModeBanner from "../features/auth/components/SupportModeBanner";

const FIVE_MIN = 5 * 60 * 1000;
const TEN_MIN = 10 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: TEN_MIN,
      retry: 1,
      retryDelay: apiRetryDelay,
      refetchOnWindowFocus: false,
    },
  },
});

// Per-resource cache tuning. Mostly-static assets stay fresh longer; live queue
// updates arrive through the realtime bridge and invalidate only affected keys.
queryClient.setQueryDefaults(["mockups"], { staleTime: FIVE_MIN });
queryClient.setQueryDefaults(["folders"], { staleTime: FIVE_MIN });
queryClient.setQueryDefaults(["folderGroups"], { staleTime: FIVE_MIN });
queryClient.setQueryDefaults(["design-campaigns"], { staleTime: FIVE_MIN });
// Niches ändern sich selten — auch nach 10 min sind sie fast immer noch gültig.
queryClient.setQueryDefaults(["design-niches"], { staleTime: TEN_MIN });

/**
 * Bundles all top-level providers in a single wrapper so App.jsx stays clean.
 *
 * Provider order (outer → inner):
 *  1. QueryClientProvider   – React Query cache
 *  2. ThemeProvider         – Dark/light theme
 *  3. I18nProvider          – Unified i18n system (translations, locale switching)
 *  4. AuthProvider          – JWT auth context (needs Router → must be below RouterProvider)
 *  5. RealtimeEventsBridge  – one authenticated event stream per browser context
 *  6. ToastProvider         – App-wide toast notifications
 */
export default function AppProviders({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <RealtimeEventsBridge />
            <ToastProvider>
              <SupportModeBanner />
              {children}
            </ToastProvider>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
